'use client';
/** @jsxImportSource @emotion/react */

import {
  type KeyboardEvent,
  type MouseEvent,
  useState,
  useEffect,
  useCallback,
} from 'react';
import React from 'react';

import { typographyClasses } from '@mui/material/Typography';

import { CoreIcon } from '../core-icon/CoreIcon';
import { IconButton } from '../icon-button/IconButton';
import { styled } from '../styled/styled';
import { Typography } from '../typography/Typography';

const counterSize = {
  container: {
    medium: '120px',
    small: '84px',
  },
  item: {
    medium: '40px',
    small: '28px',
  },
};

export type BaseCounterProps = {
  /**
   * If true, the component is disabled
   */
  disabled?: boolean;
  /**
   * If `true`, the counter will take up the full width of its container. Only applies to horizontal variant.
   *
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Maximum allowed value of the counter
   */
  max?: number;
  /**
   * Minimum allowed value of the counter
   */
  min?: number;
  /**
   * Size of the counter
   */
  size?: 'medium' | 'small';
  /**
   * The granularity with which the counter can step through values
   */
  step?: number;
  /**
   * Value of the counter
   */
  value?: number;
  /**
   * Variant of the counter
   */
  variant?: 'horizontal' | 'vertical';
  /**
   * Fires each time the counter changes
   */
  onChange?: (value: number) => void;
  /**
   * Fires each time the counter is decremented
   */
  onDecrement?: (event: KeyboardEvent | MouseEvent) => void;
  /**
   * Fires each time the counter is incremented
   */
  onIncrement?: (event: KeyboardEvent | MouseEvent) => void;
};

type WithLabel = BaseCounterProps & {
  label: string;
  labelledBy?: never;
};

type WithLabelledBy = BaseCounterProps & {
  labelledBy: string;
  label?: never;
};

export type CounterProps = WithLabel | WithLabelledBy;

export const Counter = React.forwardRef<HTMLInputElement, CounterProps>(
  (
    {
      disabled = false,
      fullWidth = false,
      label,
      labelledBy,
      max = 10,
      min = 0,
      onChange,
      onDecrement,
      onIncrement,
      size = 'medium',
      step = 1,
      value = 0,
      variant = 'horizontal',
    },
    ref
  ): React.JSX.Element => {
    const [count, setCount] = useState<number>(value);

    useEffect(() => {
      setCount(value);
    }, [value]);

    const handleCounterChange = useCallback(
      (newValue: number) => {
        if (newValue >= min && newValue <= max) {
          setCount(newValue);
          onChange && onChange(newValue);
        }
      },
      [max, min, onChange]
    );

    const handleAdd = useCallback(
      (event: KeyboardEvent | MouseEvent) => {
        handleCounterChange(count + step);
        onIncrement && onIncrement(event);
      },
      [count, handleCounterChange, onIncrement, step]
    );

    const handleSubtract = useCallback(
      (event: KeyboardEvent | MouseEvent) => {
        handleCounterChange(count - step);
        onDecrement && onDecrement(event);
      },
      [count, handleCounterChange, onDecrement, step]
    );

    // Enable up/down arrow use to increase/decrease counter
    const handleKeyPress = useCallback(
      (event: KeyboardEvent) => {
        if (event.code === 'ArrowUp') {
          handleAdd(event);
        }
        if (event.code === 'ArrowDown') {
          handleSubtract(event);
        }
      },
      [handleAdd, handleSubtract]
    );

    return (
      <>
        {/* Hidden range input for screen readers */}
        {label && <StyledHiddenLabel>{label}</StyledHiddenLabel>}
        <StyledHiddenInput
          ref={ref}
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy || undefined}
          aria-valuemax={max}
          aria-valuemin={min}
          defaultValue={count}
          disabled={disabled}
          max={max}
          min={min}
          role="slider"
          step={step}
          type="range"
          onKeyDown={handleKeyPress}
        />
        {/* Visible counter component */}
        <StyledCounterContainer
          aria-hidden="true"
          data-testid="counter"
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          variant={variant}
        >
          <StyledIconButton
            data-testid="subtract"
            disabled={disabled || count <= min}
            size={size}
            tabIndex={-1}
            onClick={handleSubtract}
          >
            <CoreIcon name="subtract" />
          </StyledIconButton>
          <Typography
            data-testid="counterLabel"
            variant={size === 'small' ? 'body2' : 'body1'}
          >
            {count}
          </Typography>
          <StyledIconButton
            data-testid="add"
            disabled={disabled || count >= max}
            size={size}
            tabIndex={-1}
            onClick={handleAdd}
          >
            <CoreIcon name="add" />
          </StyledIconButton>
        </StyledCounterContainer>
      </>
    );
  }
);

const StyledCounterContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'fullWidth',
})<Pick<CounterProps, 'disabled' | 'fullWidth' | 'size' | 'variant'>>(
  ({ disabled, fullWidth = false, size = 'medium', theme, variant }) => ({
    '&:focus::after': {
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
      content: '""',
      display: 'block',
      position: 'absolute',
    },
    [`.${typographyClasses.root}`]: {
      height: counterSize.item[size],
      padding: size === 'medium' ? theme.spacing(1) : theme.spacing(0.5),
      width: counterSize.item[size],
    },
    alignItems: 'center',
    border: `${theme.ui.counter.border.width} solid ${theme.ui.counter.border.color}`,
    borderRadius: theme.ui.counter.borderRadius,
    display: 'flex',
    justifyContent: fullWidth ? 'space-between' : 'center',
    textAlign: 'center',
    ...(disabled && {
      background: theme.ui.counter.button.disabled.background,
      color: theme.ui.counter.button.disabled.foreground,
    }),
    ...(variant === 'horizontal'
      ? {
          flexDirection: 'row',
          height: counterSize.item[size],
          width: fullWidth ? '100%' : counterSize.container[size],
        }
      : {
          flexDirection: 'column-reverse',
          height: counterSize.container[size],
          width: counterSize.item[size],
        }),
  })
);

const StyledIconButton = styled(IconButton)<Pick<CounterProps, 'size'>>(
  ({ size = 'medium', theme }) => ({
    '&:disabled': {
      background: theme.ui.counter.button.disabled.background,
      color: theme.ui.counter.button.disabled.foreground,
    },
    '@media (hover: hover)': {
      '&:hover': {
        background: theme.ui.counter.button.hover.background,
        color: theme.ui.counter.button.hover.foreground,
      },
    },
    background: theme.ui.counter.button.enabled.background,
    borderRadius: 0,
    color: theme.ui.counter.button.enabled.foreground,
    height: `calc(${counterSize.item[size]} - ${theme.ui.counter.border.width})`,
    width: `calc(${counterSize.item[size]} - ${theme.ui.counter.border.width})`,
  })
);

const StyledHiddenLabel = styled('label')(() => ({
  height: 0,
  overflow: 'hidden',
  position: 'absolute',
  width: 0,
}));

const StyledHiddenInput = styled('input')(({ theme }) => ({
  '&:focus': {
    '+ div': {
      borderRadius: `${theme.ui.focus.borderRadius}`,
      outline: `${theme.ui.focus.outlineWidth} solid ${theme.palette.action.focusOutline}`,
      outlineOffset: `${theme.ui.focus.outlineOffset}`,
    },
  },
  height: 0,
  overflow: 'hidden',
  position: 'absolute',
  width: 0,
}));

Counter.displayName = 'Counter';
